// Timezone helpers used throughout the calendar surface.
//
// Critical context: the user's preferred timezone is stored as an IANA name
// (e.g. "America/New_York") on the `me.timezone` field. The Calendar UI used
// to call `Date.prototype.toLocaleTimeString([])` without the `timeZone`
// option, which silently formatted in whatever timezone the browser happened
// to be in — wrong if you're in PST but your saved TZ is EST.
//
// Every formatter here takes the tz explicitly so callers can't forget.
//
// The two thorny pieces are the `<input type="datetime-local">` round-trip
// (the browser always interprets it as local-browser time, which we don't
// want) and DST transitions. We use a two-pass calculation that's correct
// outside the actual transition minute itself.

const TWO_DIGIT_PARTS = {
  year: "numeric" as const,
  month: "2-digit" as const,
  day: "2-digit" as const,
  hour: "2-digit" as const,
  minute: "2-digit" as const,
  hour12: false,
};

/** Format "HH:mm" in the given IANA timezone. */
export function formatTimeInTz(ts: number, tz: string): string {
  return new Date(ts).toLocaleTimeString("en-US", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Format a localized date string in the given IANA timezone. */
export function formatDateInTz(
  ts: number,
  tz: string,
  opts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" },
): string {
  return new Date(ts).toLocaleDateString("en-US", { timeZone: tz, ...opts });
}

/** Format a one-line "Mon, May 19 · 14:30, 15:00" range in the given tz. */
export function formatRangeInTz(start_ts: number, end_ts: number, tz: string): string {
  const date = formatDateInTz(start_ts, tz);
  const startTime = formatTimeInTz(start_ts, tz);
  const endTime = formatTimeInTz(end_ts, tz);
  return `${date} · ${startTime}, ${endTime}`;
}

/**
 * Convert a unix ms timestamp into a "YYYY-MM-DDTHH:mm" string formatted in
 * the given IANA timezone — suitable for the `value` of a
 * `<input type="datetime-local">`. The browser will display it as-is.
 */
export function tsToInputLocalInTz(ts: number, tz: string): string {
  // sv-SE locale formats as "YYYY-MM-DD HH:MM" — swap the space for T.
  const out = new Intl.DateTimeFormat("sv-SE", {
    timeZone: tz,
    ...TWO_DIGIT_PARTS,
  }).format(new Date(ts));
  return out.replace(" ", "T");
}

/**
 * Parse a "YYYY-MM-DDTHH:mm" string (as produced by `<input type="datetime-local">`)
 * as if the values were wall-clock times in the given IANA timezone, and
 * return the corresponding unix ms. Handles DST except at the transition
 * minute itself (where the wall-clock is ambiguous/missing).
 */
export function inputLocalToTsInTz(value: string, tz: string): number {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!m) return NaN;
  const [, y, mo, d, h, mi] = m;
  // First guess: treat the wall-clock as UTC.
  const utcGuess = Date.UTC(+y, +mo - 1, +d, +h, +mi);
  // Format that guess in tz — see what wall-clock the browser reads back.
  // The difference between guessWallClock and wantedWallClock is the offset.
  const wallClock = tsToInputLocalInTz(utcGuess, tz);
  const wallMatch = wallClock.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!wallMatch) return utcGuess;
  const [, wy, wmo, wd, wh, wmi] = wallMatch;
  const wallUtc = Date.UTC(+wy, +wmo - 1, +wd, +wh, +wmi);
  // Subtract the round-trip drift to get the true UTC instant.
  return utcGuess - (wallUtc - utcGuess);
}

/** Browser-detected fallback when we don't have a saved user TZ yet. */
export function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Validate that a string is a recognized IANA timezone name. Falsy strings
 * return false; anything Intl.DateTimeFormat accepts returns true.
 */
export function isValidTz(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    // Intl throws RangeError for invalid timezone names.
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Short suffix for displaying a tz alongside a time, e.g. "EDT" or "GMT+1".
 * Falls back to the IANA name if no short form is available.
 */
export function tzShortName(ts: number, tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      timeZoneName: "short",
    }).formatToParts(new Date(ts));
    return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    return tz;
  }
}
